import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Injectable,
  input,
  model,
  OnInit, QueryList,
  ViewChild, ViewChildren,
} from '@angular/core';
import * as uuid from 'uuid';
import { LabelOptions } from '@core/classes/options/label-options';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InputOptions } from '@shared/components';
import { TranslateService } from '@ngx-translate/core';
import { ButtonTypes, InputModes, InputTypes } from '@core/enums';
import {
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDateStruct,
  NgbInputDatepicker,
} from '@ng-bootstrap/ng-bootstrap';
import { ButtonComponent } from '@shared/components/button/button.component';
import { JsonPipe } from '@angular/common';
import moment from 'moment/moment';

export class DateOptions {
  id?: string = uuid.v4();
  label?: LabelOptions;
  placeholder?: string;
  name?: string;
  value?: string;
  inputClass?: string;
  onKeyDown?: (event: KeyboardEvent, value: NgbDateStruct & string) => void;
  onKeyUp?: (event: KeyboardEvent, value: NgbDateStruct & string) => void;
  onInput?: (el: HTMLInputElement) => any;
  onBlur?: (value: NgbDateStruct & string) => void;
  initialValue?: any;
  formControl?: FormControl<any>;
  formGroup?: FormGroup;
  formControlName?: string;
  required?: boolean | false;
  disabled?: boolean | false;
  minDate?: string;
  maxDate?: string;

  constructor(args?: DateOptions) {
    this.id = args?.id || uuid.v4();
    this.label = args?.label;
    this.placeholder = args?.placeholder || '';
    this.name = args?.name || '';
    this.value = args?.value || '';
    this.inputClass = args?.inputClass || '';
    this.onKeyDown = args?.onKeyDown;
    this.onKeyUp = args?.onKeyUp;
    this.onInput = args?.onInput;
    this.onBlur = args?.onBlur;
    this.initialValue = args?.initialValue;
    this.formControl = args?.formControl;
    this.formGroup = args?.formGroup;
    this.formControlName = args?.formControlName;
    this.required = args?.required !== undefined ? args?.required : false;
    this.disabled = args?.disabled !== undefined ? args?.disabled : false;
    this.minDate = args?.minDate;
    this.maxDate = args?.maxDate;
  }
}

@Injectable()
export class CustomAdapter extends NgbDateAdapter<string> {
  readonly DELIMITER = /[.\/-]/;

  fromModel(value: string | null): NgbDateStruct | null {
    if (value) {
      const date = value.split(this.DELIMITER);
      return {
        day: parseInt(date[2], 10),
        month: parseInt(date[1], 10),
        year: parseInt(date[0], 10),
      };
    }
    return null;
  }

  toModel(date: NgbDateStruct | null): string | null {
    return date
      ? ('0000' + date.year).slice(-4) +
          '-' +
          ('00' + date.month).slice(-2) +
          '-' +
          ('00' + date.day).slice(-2)
      : null;
  }
}

@Injectable()
export class CustomDateParserFormatter extends NgbDateParserFormatter {
  readonly DELIMITER = /\b[.\/-]\b/g;

  parse(value: string): NgbDateStruct | null {
    if (value) {
      let parsedValue: string = value.replace(this.DELIMITER, '/');
      if(moment(parsedValue, [
        'YYYY-M-D',
        'YYYY/M/D',
        'YYYY.M.D',
        'D-M-YYYY',
        'D/M/YYYY',
        'D.M.YYYY',

        'YYYY-MM-DD',
        'YYYY/MM/DD',
        'YYYY.MM.DD',
        'DD-MM-YYYY',
        'DD/MM/YYYY',
        'DD.MM.YYYY'
      ], true).isValid()) {
        let newDate = moment(parsedValue, [
          'YYYY-M-D',
          'YYYY/M/D',
          'YYYY.M.D',
          'D-M-YYYY',
          'D/M/YYYY',
          'D.M.YYYY',

          'YYYY-MM-DD',
          'YYYY/MM/DD',
          'YYYY.MM.DD',
          'DD-MM-YYYY',
          'DD/MM/YYYY',
          'DD.MM.YYYY'
        ], true);
        const date = newDate.format('D-M-YYYY').split(this.DELIMITER);
        return {
          day: parseInt(date[0], 10),
          month: parseInt(date[1], 10),
          year: parseInt(date[2], 10)
        };
      } else {
        return null;
      }
    }
    return null;
  }

  format(date: NgbDateStruct | null): string {
    return date ? ('00' + date.day).slice(-2) + '/' + ('00' + date.month).slice(-2) + '/' + ('0000' + date.year).slice(-4) : '';
  }
}

@UntilDestroy()
@Component({
  selector: 'ncp-date',
  imports: [FormsModule, ReactiveFormsModule, NgbInputDatepicker, ButtonComponent],
  templateUrl: './date.component.html',
  styleUrl: './date.component.scss',
  providers: [
    { provide: NgbDateAdapter, useClass: CustomAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
  ],
})
export class DateComponent implements OnInit, AfterViewInit {
  @ViewChild('el') public el!: NgbInputDatepicker;
  @ViewChildren('el') public els?: QueryList<NgbInputDatepicker>
  private readonly translate = inject(TranslateService);
  protected translations = this.translate.instant('date');
  protected invalidMessage: string = '';
  protected value!: NgbDateStruct & string;
  protected _minDate?: NgbDateStruct = {year: 1900, month: 1, day: 1};
  protected _maxDate?: NgbDateStruct;

  options = model<DateOptions>(new DateOptions({}));

  // options = input(new DateOptions(), {
  //   transform: (value: DateOptions) => {
  //     if (!value) {
  //       return new InputOptions({});
  //     }
  //
  //     if (value.inputClass) {
  //       value.inputClass = this.options().inputClass + ' ' + value.inputClass;
  //     }
  //
  //     if (value.initialValue) {
  //       if (value.formGroup && value.formControlName) {
  //         value.formGroup.controls[value.formControlName].setValue(value.initialValue);
  //       } else if (value.formControlName) {
  //         value.formControl?.setValue(value.initialValue);
  //       }
  //     }
  //
  //     return value;
  //   },
  // });

  get minDate() {
    return this._minDate;
  }

  get maxDate() {
    return this._maxDate;
  }

  set minDate(minDate: NgbDateStruct | undefined) {
    this._minDate = minDate;
  }

  set maxDate(maxDate: NgbDateStruct | undefined) {
    this._maxDate = maxDate;
  }

  ngOnInit(): void {
    this.reset();

    this.els?.changes.subscribe((item) => {
      console.log(item);
      this.els!.first!.minDate! = this._minDate!;
      this.els!.first!.maxDate! = this._maxDate!;
    })

    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.translations = this.translate.instant('date');
    });
  }

  ngAfterViewInit(): void {}

  onKeyDown(event: KeyboardEvent, value: NgbDateStruct & string) {
    if (this.options()?.onKeyDown) {
      this.options().onKeyDown?.(event, value);
    }
  }

  onKeyUp(event: KeyboardEvent, value: NgbDateStruct & string) {
    if (this.options()?.onKeyUp) {
      this.options()?.onKeyUp?.(event, value);
    }
  }

  onInput(input: HTMLInputElement) {
    if (this.options()?.onInput) {
      this.options().value = this.options().onInput?.(input);
      input.value = this.options().value || '';
      this.options().onInput?.(input);
      //this.options()?.formControl?.setValue(this.options().value);
    }
  }

  onBlur(value: NgbDateStruct & string) {
    console.log('dt = ' + value)
    if (this.options()?.onBlur) {
      this.options()?.onBlur?.(value);
    }
  }

  enable() {
    if(this.options().formControl) {
      this.options().formControl?.enable();
    }
  }

  disable() {
    if(this.options().formControl) {
      this.options().formControl?.disable();
    }
  }

  reset() {
    if (this.options().initialValue) {
      if (this.options().formGroup && this.options().formControlName) {
        this.options().formGroup?.controls[this.options()?.formControlName!].setValue(
          this.options().initialValue
        );
      } else if (this.options().formControlName) {
        this.options().formControl?.setValue(this.options().initialValue);
      }
    }

    if(this.options().disabled) {
      this.disable();
    }

    if(this.options().minDate) {
      this._minDate = {
        day: parseInt(moment(this.options().minDate).format('D'), 10),
        month: parseInt(moment(this.options().minDate).format('M'), 10),
        year: parseInt(moment(this.options().minDate).format('YYYY'), 10),
      }
    }

    if(this.options().maxDate) {
      this._maxDate = {
        day: parseInt(moment(this.options().maxDate).format('D'), 10),
        month: parseInt(moment(this.options().maxDate).format('M'), 10),
        year: parseInt(moment(this.options().maxDate).format('YYYY'), 10),
      }
    }
  }

  protected readonly oninput = oninput;
  protected readonly InputModes = InputModes;
  protected readonly InputTypes = InputTypes;
  protected readonly ButtonTypes = ButtonTypes;
}
